// Dutch overrides for src/data/services.js.
// Keyed by service id. Tiers keyed by their English tier name, addons by their
// English label — those stay the identity keys used in URLs and form payloads.
// Numeric fields (priceMin/priceMax) and price strings are inherited from the
// English source and intentionally not repeated here.
export const servicesNl = {
  1: {
    title: 'WEBDESIGN',
    cost: 'vanaf €1.800',
    outcome: 'Een snelle, on-brand site die je echt zelf kunt beheren.',
    bestIf: 'Je hebt een geloofwaardige, conversieklare webaanwezigheid nodig.',
    description:
      'Custom, responsive websites die lokaal verkeer omzetten in vaste klanten. Van snel ladende landingspagina’s voor vastgoed tot volledige corporate platformen — ik bouw digitale ervaringen die echte groei opleveren.',
    longDescription:
      'Van een scherpe one-pager tot een volledig custom build met motion en 3D — elk webproject start in Figma, gaat live op WordPress (met Elementor) of als een volledig custom React-build, en landt met on-page SEO ingebakken. Je krijgt een site die snel is, toegankelijk, en makkelijk zelf te updaten.',
    process: [
      'Discovery-call & scope',
      'Wireframes + Figma-design',
      'Build & integraties',
      'Launch + 30d support',
    ],
    tiers: {
      Starter: {
        timeline: '1–2 weken',
        bestFor: 'One-pagers, freelancers, lokale zaken',
        features: [
          'Tot 5 pagina’s, volledig responsive',
          'On-page SEO + meta tags',
          'Contactformulier + Google Analytics',
          '1 revisieronde',
          '30 dagen support na launch',
        ],
        notIncluded: [
          'Geen custom 3D WebGL-scènes',
          'Geen custom CMS of geavanceerde integraties',
          'Geen custom kinetische micro-interacties',
        ],
      },
      Standard: {
        timeline: '3–5 weken',
        bestFor: 'Groeiende bedrijven die een CMS nodig hebben',
        features: [
          'Tot 10 pagina’s met custom design',
          'WordPress + Elementor of een volledig custom React-build',
          'Lichte interacties & animaties',
          'Integraties (Stripe, Calendly, Mailchimp)',
          '2 revisierondes',
          '30 dagen support na launch',
        ],
        notIncluded: [
          'Geen Three.js / WebGL 3D-canvas (enkel Premium)',
          'Geen custom shader / VFX-motion (enkel Premium)',
          'Geen diepgaande Core Web Vitals 99+ tuning',
        ],
      },
      Premium: {
        timeline: '6–10+ weken',
        bestFor: 'Merken die willen imponeren vanaf het eerste frame',
        features: [
          'Volledig custom build, geen templates',
          'Interactieve Three.js / R3F 3D-scènes',
          'Custom micro-interacties & GSAP',
          'Performance-audit & Core Web Vitals-tuning',
          '3 revisierondes',
          '60 dagen support na launch',
        ],
        notIncluded: [],
      },
    },
    addons: {
      'Copywriting per page': {
        label: 'Copywriting per pagina',
        indicative: '+€80 / pagina',
      },
      'Hosting & maintenance retainer': {
        label: 'Hosting & onderhoud retainer',
        indicative: 'vanaf €95 / maand',
      },
      'Multilingual setup (per extra lang.)': {
        label: 'Meertalige setup (per extra taal)',
        indicative: '+€350 / taal',
      },
    },
  },
  2: {
    title: 'MOTION DESIGN',
    cost: 'vanaf €1.800',
    outcome: 'Scroll-stopping motion & 3D die je merk een hartslag geven.',
    bestIf: 'Je wilt beweging, 3D of VFX waar mensen voor stoppen.',
    description:
      'Breng je merk tot leven met dynamische motion graphics. Ik maak pakkende social loops, campagnevideo’s en geanimeerde assets die aandacht vangen en je diensten helder uitleggen aan je publiek.',
    longDescription:
      'Van een strakke logo-animatie tot een volledige campagnefilm — animatie, kinetische typografie en 2D/3D-motion gebouwd in After Effects, 3ds Max en Blender. Ook 3D hoort hier thuis: product motion, characterwerk en scene-driven storytelling, afgewerkt met zorg voor timing, sound-readiness en exports per platform.',
    process: [
      'Brief + moodboard',
      'Storyboard / kinetische frames',
      'Animatiepass + look-dev',
      'Finale render & exports',
    ],
    tiers: {
      Starter: {
        timeline: '1–2 weken',
        bestFor: 'Logo-animaties, kinetic type, lichte edits op bestaande footage',
        features: [
          'Geanimeerd logo (3–5s)',
          'Lichte motion-edits op bestaande video / audio',
          'Kinetic type of eenvoudige titelkaarten',
          'Meerdere aspect ratio’s (vierkant, verticaal, breed)',
          '1 revisieronde',
        ],
      },
      'Campaign motion': {
        timeline: '3–5 weken',
        bestFor: 'Brand films, product motion, kinetic type-pieces',
        features: [
          'Geanimeerde piece tot 30s',
          '2D/3D-mix met motion graphics',
          'Storyboard + 2 designframes',
          '2 revisierondes',
          'Color grade + finishing',
        ],
      },
      '3D motion': {
        timeline: '6–10+ weken',
        bestFor: 'Volledige 3D-scènes, character- / productpieces, launchfilms',
        features: [
          'Volledige 3D-scène gebouwd in 3ds Max / Blender',
          'Look-dev, lighting & materialen',
          'Custom rigging of product motion',
          'Motion graphics + typografie',
          'Color grade + finishing',
        ],
      },
    },
    addons: {
      'Royalty-free music + sound mix': {
        label: 'Rechtenvrije muziek + sound mix',
        indicative: '+€150',
      },
      'Print-ready stills from scene': {
        label: 'Print-klare stills uit de scène',
        indicative: '+€150',
      },
      'Extra aspect ratios (TikTok / Reels / Shorts)': {
        label: 'Extra aspect ratio’s (TikTok / Reels / Shorts)',
        indicative: '+€120',
      },
      'Extra revision round': {
        label: 'Extra revisieronde',
        indicative: '+€180',
      },
    },
  },
  3: {
    title: 'BRANDING',
    cost: 'vanaf €1.500',
    outcome: 'Een volledig identiteitssysteem dat overal consistent blijft.',
    bestIf: 'Je launcht of relauncht en hebt een volledig systeem nodig.',
    description:
      'Volledige brandsystemen en guidelines — typografie, kleur, tone of voice en toepassingen — zodat elk touchpoint consistent blijft, ver voorbij het logo.',
    longDescription:
      'Branding gaat veel verder dan een logo. Het volledige systeem — typografie, kleur, tone of voice, fotografierichting, en een guideline die alles consistent houdt, of het nu een billboard is, een Instagram-post of een visitekaartje.',
    process: [
      'Discovery + concurrentie in kaart',
      'Visuele richting & moodboard',
      'Identiteitsdesign + toepassingen',
      'Oplevering van de guideline',
    ],
    tiers: {
      Mini: {
        timeline: '2–3 weken',
        bestFor: 'Side projects, side hustles, kleine merken',
        features: [
          'Logo + 2 varianten',
          'Kleur- & typesysteem (hexcodes, fonts, weights)',
          'One-pager brand usage PDF (do’s/don’ts)',
          'Templates voor social profielen (IG post, IG story)',
        ],
      },
      Standard: {
        timeline: '4–6 weken',
        bestFor: 'Startups, scale-ups, gevestigde kmo’s',
        features: [
          'Logo + varianten (primair, secundair, mono)',
          'Volledig kleur- & typesysteem',
          'Brand guideline PDF (15+ pagina’s)',
          'Visitekaartje + briefpapier design',
          'Social templates kit (5 formaten)',
          '2 revisierondes',
        ],
      },
      Comprehensive: {
        timeline: '7–10+ weken',
        bestFor: 'Merken die relaunchen met gewicht',
        features: [
          'Logosysteem (primair, secundair, mono, icoon)',
          'Volledig kleur- & typesysteem',
          'Brand guideline PDF (25+ pagina’s, do’s/don’ts, toepassingen)',
          'Naming + ondersteuning bij brandstrategie',
          'Tone of voice & messaging guide',
          'Drukwerk (visitekaartjes, signage, pitch deck template)',
          'Stijlgids voor fotografie & illustratie',
          'Geanimeerde logo bumper (3–5s intro/outro voor video)',
        ],
      },
    },
    addons: {
      'Naming workshop': {
        label: 'Naming workshop',
        indicative: '+€450',
      },
      'Pitch deck template': {
        label: 'Pitch deck template',
        indicative: '+€350',
      },
      'Print management (cards, signage)': {
        label: 'Drukwerkbegeleiding (kaartjes, signage)',
        indicative: 'Kostprijs + 15%',
      },
      'Brand voice & messaging guide': {
        label: 'Brand voice & messaging guide',
        indicative: '+€500',
      },
    },
  },
  4: {
    title: 'LOGO DESIGN',
    cost: 'vanaf €450',
    outcome: 'Een onderscheidend merkteken in elk formaat dat je ooit nodig hebt.',
    bestIf: 'Je hebt gewoon één scherp, veelzijdig merkteken nodig — snel.',
    description:
      'Val op in de markt met een sterke, memorabele merkidentiteit. Ik lever complete visuele systemen — van onderscheidend logo design tot een volledige huisstijl — zodat je bedrijf een blijvende, professionele indruk maakt.',
    longDescription:
      'Een gefocust branding- & logoproject — snel, zonder franjes. Opties eerst verkend op papier voor ze het scherm raken, daarna samen bijgeschaafd tot het merkteken en de bijhorende styling juist voelen. Voor volledige systemen en meerjarige guidelines: zie de Branding-dienst.',
    process: [
      'Brief & inspiratiecall',
      '3 conceptrichtingen',
      'Verfijning van de gekozen richting',
      'Finale oplevering in alle formaten',
    ],
    tiers: {
      'Logo only': {
        timeline: '1 week',
        bestFor: 'Snelle launches, MVP’s',
        features: [
          '3 unieke concepten',
          '2 revisierondes',
          'Finaal logo in SVG, PNG, PDF',
          'Light- + dark-varianten',
        ],
      },
      'Logo + mini brand': {
        timeline: '2 weken',
        bestFor: 'Bedrijven die de basis op orde willen',
        features: [
          '3 unieke logoconcepten',
          '2 revisierondes',
          'Finaal logo in SVG, PNG, PDF',
          'Light- + dark-varianten',
          'Kleurenpalet + typografie',
          'One-pager brand mini-guide (logo, palet, type, 2–3 do’s/don’ts)',
          'E-mailhandtekening',
        ],
      },
      'Logo system': {
        timeline: '3 weken',
        bestFor: 'Merken die flexibiliteit nodig hebben',
        features: [
          '3 unieke logoconcepten + 2 revisierondes',
          'Primaire + secundaire merktekens',
          'Wordmark + monogram + icoon',
          'Light- + dark-varianten, alle formaten (SVG, PNG, PDF)',
          'Geanimeerde logo bumper (3–5s intro/outro voor video)',
          'Gids met do’s & don’ts voor gebruik',
        ],
      },
    },
    addons: {
      'Animated logo (3s bumper)': {
        label: 'Geanimeerd logo (3s bumper)',
        indicative: '+€350',
      },
      'Business card design': {
        label: 'Visitekaartje design',
        indicative: '+€120 – €180',
      },
      'Email signature design': {
        label: 'E-mailhandtekening design',
        indicative: '+€80',
      },
      'Brand mini-guide (PDF)': {
        label: 'Brand mini-guide (PDF)',
        indicative: '+€250',
      },
    },
  },
};

export default servicesNl;
